import React, { useState } from 'react'
import ReactModal from 'react-modal'
import '../../styles/modals/ConfirmationFeedDeleteModal.css'
import { useTranslation } from 'react-i18next'
import ConfirmImage from '../../assets/svg/ConfirmImage.webp'
import Loading from '../Loading'

// Loading defaults to the yellow highlightColor, which all but disappears on
// the outlined buttons. Mirror each button's text color instead (tailwind.config.js).
const DELETE_RED = '#FF747F'
const DEEP_PURPLE = '#5E639B'

/**
 * Cancel / confirm dialog. Pass `secondaryConfirmation` (+ `secondaryButtonText`)
 * to offer a second destructive choice, e.g. "delete this run only" versus
 * "delete this and all scheduled runs" for recurring content; the buttons then
 * stack vertically so the two choices read as sentences.
 */
function ConfirmationModal({
    modalIsOpen,
    afterOpenModal,
    closeModal,
    confirmation,
    message,
    subMessage = '',
    confirmButtonText = 'OK',
    destructive = true,
    secondaryConfirmation = null,
    secondaryButtonText = '',
}) {
    const { t } = useTranslation()

    // Which action is in flight, so only the clicked button swaps to a spinner
    // while the other two are merely disabled: 'primary' | 'secondary' | null.
    const [pending, setPending] = useState(null)
    const hasSecondary = typeof secondaryConfirmation === 'function'
    const busy = pending !== null

    // In the stacked layout the container is a column, so `flex-1` would apply
    // to the *height* and wipe out h-[55px]. Only stretch on the row axis.
    const actionClass = hasSecondary ? 'w-full shrink-0' : 'flex-1'

    React.useEffect(() => {
        const home = document.getElementById('home')
        if (!home) return

        if (modalIsOpen) {
            ReactModal.setAppElement('#root')
            home.style.filter = 'blur(5px)'
        } else {
            home.style.filter = 'unset'
        }
    }, [modalIsOpen])

    const runAction = (key, action) => () => {
        if (busy) return
        setPending(key)
        action()
        // closeModal() is left to the caller, which knows when the request settled.
    }

    return (
        <ReactModal
            isOpen={modalIsOpen}
            onAfterOpen={afterOpenModal}
            onRequestClose={closeModal}
            contentLabel="Example Modal"
            onAfterClose={() => {
                setPending(null)
            }}
            style={{
                overlay: {
                    zIndex: 102,
                },
                content: {
                    width: 600,
                    // Grows with the stacked layout; the 500px floor lives on
                    // .confirmation-dialog-content so the card still paints it.
                    height: 'auto',
                    padding: 0,
                    opacity: 1,
                    minWidth: 600,
                    marginTop: '2%',
                    backgroundColor: 'transparent',
                    maxHeight: '90%',
                    border: 'none',
                    margin: 'auto',
                    boxShadow: 'none',
                    overflowY: 'auto',
                },
            }}
        >
            <div className="confirmation-dialog-content bg-whiteContrast rounded-xl flex flex-col items-center">
                <img src={ConfirmImage} alt="Confirm" />
                <p className="text-deepPurple text-xl font-bold text-center">{message}</p>
                {!!subMessage && <p className="text-mudPurple text-sm mt-2 text-center">{subMessage}</p>}

                <div className={`confirmation-dialog-actions flex items-center w-full pt-6 ${hasSecondary ? 'flex-col gap-3' : 'flex-row gap-5'}`}>
                    <button
                        type="button"
                        id="confirmation-modal-cancel-button"
                        className={`bg-deepPurple pointer flex ${actionClass} items-center justify-center text-highlightColor text-base font-semibold rounded-xl h-[55px] transition-all !duration-[250ms] ease-in-out hover:bg-highlightColor hover:text-deepPurple`}
                        disabled={busy}
                        onClick={() => closeModal()}
                    >
                        {t('cancel')}
                    </button>
                    {hasSecondary && (
                        <button
                            type="button"
                            id="confirmation-modal-secondary-button"
                            className={`border pointer flex ${actionClass} items-center justify-center text-base font-semibold rounded-xl h-[55px] transition-all !duration-[250ms] ease-in-out border-deleteRed text-deleteRed hover:bg-deleteRed hover:text-white`}
                            disabled={busy}
                            onClick={runAction('secondary', secondaryConfirmation)}
                        >
                            {pending === 'secondary' ? <Loading primaryColor={DELETE_RED} /> : secondaryButtonText ? t(secondaryButtonText) : ''}
                        </button>
                    )}
                    <button
                        type="button"
                        id="confirmation-modal-confirm-button"
                        className={`border pointer flex ${actionClass} items-center justify-center text-base font-semibold rounded-xl h-[55px] transition-all !duration-[250ms] ease-in-out ${
                            destructive
                                ? 'border-deleteRed text-deleteRed hover:bg-deleteRed hover:text-white'
                                : 'border-deepPurple text-deepPurple hover:bg-deepPurple hover:text-highlightColor'
                        }`}
                        disabled={busy}
                        onClick={runAction('primary', confirmation)}
                    >
                        {pending === 'primary' ? <Loading primaryColor={destructive ? DELETE_RED : DEEP_PURPLE} /> : t(confirmButtonText)}
                    </button>
                </div>
            </div>
        </ReactModal>
    )
}

export default ConfirmationModal
